/**
 * Recuperação de deploy: como o cliente sai de uma versão antiga presa em cache.
 *
 * Contexto do problema (Cloudflare Pages + Vite):
 *   - o build gera chunks com hash no nome (`TicketsPage-ABC123.js`);
 *   - a cada deploy os hashes mudam e os arquivos antigos deixam de existir;
 *   - o Pages NÃO devolve 404 para um asset inexistente: ele cai no fallback de
 *     SPA e devolve o `index.html` com status 200 e `Content-Type: text/html`.
 *     Verificado em produção — por isso o erro que o usuário vê é de MIME type
 *     ("Expected a JavaScript module script..."), não um 404;
 *   - um `window.location.reload()` simples re-navega para a MESMA URL, então
 *     todas as camadas de cache (disco do browser, proxy corporativo, PWA
 *     instalado, service worker legado) têm direito a devolver o documento
 *     antigo de novo — é exatamente por isso que "clicar em atualizar" não
 *     resolve para alguns clientes.
 *
 * A solução aqui tem três partes:
 *   1. `hardReload()` — limpa Cache Storage / service workers e re-navega com
 *      um parâmetro novo na query, o que nenhuma camada de cache consegue
 *      responder com conteúdo velho.
 *   2. `installChunkErrorRecovery()` — intercepta a falha de chunk e dispara o
 *      hard reload sozinho, antes de o usuário ver qualquer tela de erro.
 *   3. `checkForNewVersion()` — sonda `/version.json` para avisar do deploy
 *      novo de forma proativa, sem esperar o usuário esbarrar num chunk morto.
 */

// Marca do último reload automático. Serve de trava anti-loop: se o deploy
// estiver realmente quebrado, o app recarrega uma vez e depois deixa o erro
// aparecer, em vez de entrar em loop infinito de reload.
const LAST_AUTO_RELOAD_KEY = 'appUpdate:lastAutoReload';
const MIN_INTERVAL_BETWEEN_AUTO_RELOADS_MS = 60_000;
const CACHE_BUSTER_PARAM = '_v';

// sessionStorage pode lançar (modo anônimo com cookies bloqueados, iframe com
// storage particionado). Nesses casos degradamos para "pode recarregar".
const readLastAutoReload = (): number => {
    try {
        return Number(sessionStorage.getItem(LAST_AUTO_RELOAD_KEY)) || 0;
    } catch {
        return 0;
    }
};

const writeLastAutoReload = (timestamp: number) => {
    try {
        sessionStorage.setItem(LAST_AUTO_RELOAD_KEY, String(timestamp));
    } catch {
        // sem storage não há trava anti-loop — segue mesmo assim
    }
};

const clearLastAutoReload = () => {
    try {
        sessionStorage.removeItem(LAST_AUTO_RELOAD_KEY);
    } catch {
        // idem
    }
};

const canAutoReload = (): boolean => {
    const last = readLastAutoReload();
    return (
        !last || Date.now() - last > MIN_INTERVAL_BETWEEN_AUTO_RELOADS_MS
    );
};

/**
 * Recarrega o app furando todas as camadas de cache.
 *
 * Diferente de `window.location.reload()`, aqui a URL muda (ganha um
 * `?_v=<timestamp>`), então o browser é obrigado a buscar o documento na
 * origem. Usa `replace` para que o documento quebrado saia do histórico — o
 * botão "voltar" não devolve o usuário para a tela de erro.
 */
export const hardReload = async (): Promise<void> => {
    writeLastAutoReload(Date.now());

    try {
        // Caches do PWA / Cache Storage, se algum dia existiram nesta origem.
        if ('caches' in window) {
            const keys = await caches.keys();
            await Promise.all(keys.map((key) => caches.delete(key)));
        }

        // Hoje o app não registra service worker, mas clientes antigos podem
        // ainda carregar um registrado por versões passadas — e um SW com
        // precache do index.html serve HTML velho para sempre, inclusive no F5.
        if ('serviceWorker' in navigator) {
            const registrations =
                await navigator.serviceWorker.getRegistrations();
            await Promise.all(registrations.map((reg) => reg.unregister()));
        }
    } catch {
        // Melhor esforço: falha na limpeza nunca pode impedir o reload.
    }

    const url = new URL(window.location.href);
    url.searchParams.set(CACHE_BUSTER_PARAM, Date.now().toString(36));
    window.location.replace(url.toString());
};

// Depois que o app sobe, o `_v` já cumpriu o papel: tirar da barra de endereço
// evita que ele vaze para links compartilhados e para o histórico.
const cleanCacheBusterFromUrl = () => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(CACHE_BUSTER_PARAM)) return;

    url.searchParams.delete(CACHE_BUSTER_PARAM);
    window.history.replaceState(
        window.history.state,
        '',
        `${url.pathname}${url.search}${url.hash}`
    );
};

/**
 * Faz o app se curar sozinho quando um chunk lazy não carrega por causa de
 * deploy novo. O Vite emite `vite:preloadError` nesse cenário; sem tratamento,
 * o erro sobe para o ErrorBoundary e vira tela de erro para o usuário.
 */
export const installChunkErrorRecovery = () => {
    cleanCacheBusterFromUrl();

    // Se o app ficou de pé por mais tempo que a janela anti-loop, a recuperação
    // deu certo: zera a trava para que uma falha futura nesta mesma aba (que
    // pode ficar aberta por dias) volte a se curar sozinha. Um loop real
    // recarrega bem antes disso e nunca chega neste timer.
    window.setTimeout(
        clearLastAutoReload,
        MIN_INTERVAL_BETWEEN_AUTO_RELOADS_MS
    );

    window.addEventListener('vite:preloadError', (() => {
        // Sem `preventDefault()` de propósito: o erro continua subindo para o
        // ErrorBoundary, que já sabe reconhecê-lo e mostra "Nova versão
        // disponível". Se o `preventDefault` fosse chamado, o `__vitePreload`
        // resolveria com `undefined` e o React quebraria com um erro genérico,
        // que a tela de erro não saberia interpretar. Como o hard reload
        // acontece em paralelo, na prática o usuário só vê a navegação.
        //
        // Se já recarregamos há menos de um minuto, o chunk está falhando por
        // outro motivo que não cache: paramos aqui e deixamos o usuário ver a
        // tela de erro com o botão, em vez de entrar em loop de reload.
        if (!canAutoReload()) return;

        void hardReload();
    }) as EventListener);
};

type VersionManifest = {
    version: string;
    buildId: string;
};

/**
 * Consulta `/version.json` (publicado a cada build) e diz se o servidor já está
 * numa versão diferente da que este tab carregou.
 */
export const checkForNewVersion = async (): Promise<boolean> => {
    try {
        const response = await fetch('/version.json', {
            cache: 'no-store',
            headers: { Accept: 'application/json' }
        });

        if (!response.ok) return false;

        // Se o asset sumiu, o Pages devolve o index.html com status 200 — daí a
        // checagem de content-type antes de tentar interpretar como JSON.
        const contentType = response.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) return false;

        const manifest = (await response.json()) as VersionManifest;
        return Boolean(manifest.buildId) && manifest.buildId !== __BUILD_ID__;
    } catch {
        // Offline ou rede instável: não é sinal de versão nova.
        return false;
    }
};
